import React from 'react'
import { Placeholder } from 'semantic-ui-react'
import { noop } from 'lodash'
import classnames from 'classnames/bind'
import type { ReoOffer, TrusteeContract } from '@adc/sdk/types'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import {
  AddressInfoLine,
  CardImage,
  ListingCardIndicator,
} from '../../listing/listingCard/cardParts'
import SaleInfo from '../saleInfo'
import styles from './styles.scss'
import { SlottableComponent } from '../../../types'

const cx = classnames.bind(styles)

const renderPlaceholder = () => (
  <div className={cx('property-value-container')}>
    <Placeholder className={cx('placeholder-main')}>
      <Placeholder.Header>
        <Placeholder.Line length='short' />
      </Placeholder.Header>
      <Placeholder.Paragraph style={{ width: '100%' }}>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Paragraph>
    </Placeholder>
  </div>
)

interface ClosingCardProps extends SlottableComponent<'action' | 'details'> {
  className?: string
  elmId?: string
  closingMethod: ReoOffer | TrusteeContract
  loading?: boolean
  disabled?: boolean
  featureFlags: object
}

const ClosingCard = ({
  className = '',
  elmId = 'listing_card',
  closingMethod,
  loading = false,
  disabled = false,
  slots,
  featureFlags,
}: ClosingCardProps) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const listing = closingMethod.listing

  let soldOn = null
  if (closingMethod.__typename === 'TrusteeContract') {
    soldOn = closingMethod.created_date
  } else if (closingMethod.__typename === 'ReoOffer') {
    soldOn = closingMethod.sold_time
  }

  return (
    <div className={cx('root', { 'root-card-disabled': disabled }, className)}>
      <div
        className={cx('base-card', className)}
        data-elm-id={`property_card_${elmId}`}
      >
        <CardImage
          // @ts-ignore not typed yet
          listing={listing}
          elmId={elmId}
          showSave={false}
          showImageCount={false}
          onSaveToggle={noop}
          featureFlags={featureFlags}
          loading={loading}
          maxBadges={0}
          disabled={disabled}
          className={cx('card-image')}
        />

        <div className={cx('property-card-body')}>
          <div className={cx('card-cta', 'desktop')}>{slots?.action?.()}</div>
          {loading ? (
            renderPlaceholder()
          ) : (
            <>
              <SaleInfo
                listingStatusGroup={listing.listing_status_group}
                soldAmount={closingMethod.winning_bid_amount}
                soldOn={soldOn}
                elmId={`${elmId}_sold_value`}
              />
              <AddressInfoLine
                // @ts-ignore next
                address={listing.formatted_address || undefined}
                elmId={`${elmId}_address`}
                isCancelled={disabled}
                className='u-mb-1'
              />

              <ListingCardIndicator
                listing={listing}
                elmId={`${elmId}_listing_indicator`}
                className={cx('asset-info')}
              />
            </>
          )}
          <div className={cx('card-cta')}>{slots?.action?.()}</div>
        </div>
      </div>
      <div>{slots?.details?.()}</div>
    </div>
  )
}

const LISTING_FRAGMENT = `
fragment ContractListing on Listing {
  listing_id
  listing_status
  listing_status_label(intent: SEARCH)
  listing_status_group
  primary_property {
    property_id
  }
  listing_page_path
  primary_photo
  listing_configuration {
    asset_type
    product_type
    is_third_party_online
  }
  formatted_address(format: DOUBLE_LINE)
  venue {
    venue_type
  }
  event {
    trustee_sale
  }
}
`
const REO_OFFER_FRAGMENT = `
  fragment ReoOfferDetails on ReoOffer {
    offer_id
    sold_time
    __typename
    winning_bid_amount
    listing {
      ...ContractListing
    }
  }
`

const TRUSTEE_CONTRACT_FRAGMENT = `
  fragment TrusteeContractDetails on TrusteeContract {
    trustee_contract_id
    created_date
    __typename
    winning_bid_amount
    listing {
      ...ContractListing
    }
  }
`
/**
 * This is a little different than our usual pattern as we have multiple fragments.
 */
ClosingCard.ContractListingFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: LISTING_FRAGMENT,
  [GRAPH_API_FRAGMENT_REFERENCE]: `...ContractListing`,
}

ClosingCard.TrusteeContractFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: TRUSTEE_CONTRACT_FRAGMENT,
  [GRAPH_API_FRAGMENT_REFERENCE]: `...TrusteeContractDetails`,
}

ClosingCard.ReoOfferFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: REO_OFFER_FRAGMENT,
  [GRAPH_API_FRAGMENT_REFERENCE]: `...ReoOfferDetails`,
}

export default ClosingCard
